import React, { useState, useEffect, useCallback } from 'react';
import { incomeAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { formatAmount } from '../../utils/formatters';
import { formatDate } from '../../utils/dateUtils';
import Toggle from '../common/Toggle';
import CustomSelect from '../common/CustomSelect';
import TextInput from '../common/TextInput';
import CurrencyInput from '../common/CurrencyInput';
import DatePicker from '../DatePicker';
import BaseModal from '../common/BaseModal';
import {
  Plus,
  Edit,
  Trash2,
  DollarSign,
  Loader2,
  Wallet,
  AlertTriangle,
  CheckCircle,
  Users
} from 'lucide-react';

// Helper to calculate monthly equivalent
function calculateMonthlyAmount(amount, frequency) {
  const numAmount = parseFloat(amount) || 0;
  switch (frequency) {
    case 'weekly':
      return numAmount * 52 / 12;
    case 'biweekly':
      return numAmount * 26 / 12;
    case 'semimonthly':
      return numAmount * 2;
    case 'monthly':
      return numAmount;
    default:
      return numAmount;
  }
}

// Helper to get frequency label
function getFrequencyLabel(frequency) {
  const labels = {
    weekly: 'Weekly',
    biweekly: 'Every 2 weeks',
    semimonthly: 'Twice a month',
    monthly: 'Monthly'
  };
  return labels[frequency] || frequency;
}

function IncomeSettings({ user }) {
  const { success: showSuccess, error: showError } = useToast();
  const [incomeList, setIncomeList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingShare, setTogglingShare] = useState(null);

  const isInHousehold = !!user?.household_id;

  // Load income data
  const loadIncome = useCallback(async () => {
    setLoading(true);
    try {
      const response = await incomeAPI.getAll();
      setIncomeList(response.data || []);
    } catch (error) {
      console.error('Failed to load income:', error);
      showError('Failed to load income data');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadIncome();
  }, [loadIncome]);

  // Calculate total monthly income
  const totalMonthlyIncome = incomeList
    .filter(i => i.is_active)
    .reduce((sum, income) => sum + calculateMonthlyAmount(income.amount, income.pay_frequency), 0);

  // Handle add/edit income
  const handleSaveIncome = async (formData) => {
    setSaving(true);
    try {
      if (editingIncome) {
        await incomeAPI.update(editingIncome.id, formData);
        showSuccess('Income updated successfully');
      } else {
        await incomeAPI.create(formData);
        showSuccess('Income added successfully');
      }
      setShowAddModal(false);
      setEditingIncome(null);
      await loadIncome();
    } catch (error) {
      console.error('Failed to save income:', error);
      showError(error.response?.data?.error || 'Failed to save income');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDeleteIncome = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await incomeAPI.delete(deleteConfirm.id);
      showSuccess('Income deleted successfully');
      setDeleteConfirm(null);
      await loadIncome();
    } catch (error) {
      console.error('Failed to delete income:', error);
      showError('Failed to delete income');
    } finally {
      setDeleting(false);
    }
  };

  // Handle toggle share with household
  const handleToggleShare = async (income) => {
    setTogglingShare(income.id);
    try {
      await incomeAPI.update(income.id, {
        share_with_household: !income.share_with_household
      });
      showSuccess(income.share_with_household 
        ? 'Income hidden from household' 
        : 'Income shared with household'
      );
      await loadIncome();
    } catch (error) {
      console.error('Failed to toggle sharing:', error);
      showError('Failed to update sharing settings');
    } finally {
      setTogglingShare(null);
    }
  };

  if (loading) {
    return (
      <div className="settings-section">
        <h2>Income</h2>
        <div className="loading-state">
          <Loader2 size={24} className="spinner" />
          <span>Loading income data...</span>
        </div>
      </div>
    );
  }

  const activeCount = incomeList.filter(i => i.is_active).length;

  return (
    <div className="settings-section">
      <h2>Income</h2>
      <p className="section-description">
        Set up your income sources to track your pay schedule and budget more effectively
      </p>

      {/* Income List or Empty State */}
      {incomeList.length > 0 ? (
        <>
          {/* Summary */}
          <div className="settings-group">
            <div className="connected-account-item connected">
              <div className="account-info">
                <div className="account-icon">
                  <Wallet size={24} />
                </div>
                <div className="account-details">
                  <h4>Estimated Monthly Income</h4>
                  <p className="account-email">
                    {formatAmount(totalMonthlyIncome)} from {activeCount} active source{activeCount !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <div className="account-actions">
                <button 
                  className="btn-secondary btn-sm"
                  onClick={() => {
                    setEditingIncome(null);
                    setShowAddModal(true);
                  }}
                >
                  <Plus size={14} /> Add Income
                </button>
              </div>
            </div>
          </div>

          {/* Income Sources List */}
          <div className="settings-group">
            <h3>Your Income Sources</h3>
            <p className="setting-description">Manage your income sources and pay schedules</p>
            
            <div className="connected-accounts-list">
              {incomeList.map((income) => {
                const monthlyAmount = calculateMonthlyAmount(income.amount, income.pay_frequency);
                return (
                  <div 
                    key={income.id} 
                    className={`connected-account-item ${income.is_active ? 'linked' : ''}`}
                  >
                    <div className="account-info">
                      <div className="account-icon">
                        <DollarSign size={24} />
                      </div>
                      <div className="account-details">
                        <h4>
                          {income.income_name}
                          {!income.is_active && (
                            <span style={{ 
                              marginLeft: '8px',
                              padding: '2px 8px',
                              fontSize: '0.7rem',
                              background: 'var(--background-color)',
                              borderRadius: '10px',
                              color: 'var(--text-tertiary)'
                            }}>
                              Inactive
                            </span>
                          )}
                          {income.share_with_household && isInHousehold && (
                            <span style={{ 
                              marginLeft: '8px',
                              padding: '2px 8px',
                              fontSize: '0.7rem',
                              background: 'rgba(34, 197, 94, 0.1)',
                              borderRadius: '10px',
                              color: 'var(--success-color)'
                            }}>
                              <Users size={10} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                              Shared
                            </span>
                          )}
                        </h4>
                        <p className="account-email">
                          {getFrequencyLabel(income.pay_frequency)} · Next: {formatDate(income.next_pay_date)}
                        </p>
                        <p className="account-status">
                          {formatAmount(income.amount)}/paycheck · <span style={{ color: 'var(--success-color)', fontWeight: 500 }}>{formatAmount(monthlyAmount)}/mo</span>
                        </p>
                      </div>
                    </div>
                    <div className="account-actions">
                      <button
                        className="btn-secondary btn-sm"
                        onClick={() => {
                          setEditingIncome(income);
                          setShowAddModal(true);
                        }}
                      >
                        <Edit size={14} /> Edit
                      </button>
                      <button
                        className="btn-danger-outline btn-sm"
                        onClick={() => setDeleteConfirm(income)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Household Sharing Settings - Only show if user is in a household */}
          {isInHousehold && incomeList.length > 0 && (
            <div className="settings-group">
              <h3>Household Sharing</h3>
              <p className="setting-description">
                Choose which income sources to share with your household members
              </p>
              
              <div className="toggle-group">
                {incomeList.map((income) => (
                  <div key={income.id} className="toggle-item">
                    <div className="toggle-info">
                      <span className="toggle-label">{income.income_name}</span>
                      <p className="toggle-description">
                        {income.share_with_household 
                          ? 'Visible to household members' 
                          : 'Only visible to you'
                        }
                      </p>
                    </div>
                    <Toggle
                      checked={income.share_with_household || false}
                      onChange={() => handleToggleShare(income)}
                      disabled={togglingShare === income.id}
                    />
                  </div>
                ))}
              </div>
              
              <div className="connected-accounts-hint" style={{ marginTop: 'var(--spacing-md)' }}>
                <Users size={16} />
                <span>Shared income will be included in household totals</span>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="settings-group">
          <div className="loading-state" style={{ flexDirection: 'column', padding: 'var(--spacing-2xl)' }}>
            <Wallet size={48} style={{ color: 'var(--text-tertiary)', marginBottom: 'var(--spacing-md)' }} />
            <h4 style={{ margin: '0 0 var(--spacing-xs)' }}>No income sources yet</h4>
            <p style={{ color: 'var(--text-secondary)', margin: '0 0 var(--spacing-lg)', textAlign: 'center' }}>
              Add your income to track your pay schedule and budget more effectively.
            </p>
            <button 
              className="btn-primary"
              onClick={() => {
                setEditingIncome(null);
                setShowAddModal(true);
              }}
            >
              <Plus size={16} /> Add Your First Income
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <IncomeModal
          income={editingIncome}
          onClose={() => {
            setShowAddModal(false);
            setEditingIncome(null);
          }}
          onSave={handleSaveIncome}
          saving={saving}
        />
      )}

      {/* Delete Confirmation Modal */}
      <BaseModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Income"
        icon={<AlertTriangle size={20} className="modal-icon danger" />}
        size="small"
        footer={
          <>
            <button 
              className="btn-secondary" 
              onClick={() => setDeleteConfirm(null)}
              disabled={deleting}
            >
              Cancel
            </button>
            <button 
              className="btn-danger"
              onClick={handleDeleteIncome}
              disabled={deleting}
            >
              {deleting ? (
                <><Loader2 size={16} className="spinner" /> Deleting...</>
              ) : (
                'Delete'
              )}
            </button>
          </>
        }
      >
        <div className="modal-body">
          <p>Are you sure you want to delete <strong>{deleteConfirm?.income_name}</strong>?</p>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>This action cannot be undone.</p>
        </div>
      </BaseModal>
    </div>
  );
}

// Income Add/Edit Modal Component
function IncomeModal({ income, onClose, onSave, saving }) {
  const [formData, setFormData] = useState({
    income_name: income?.income_name || 'Primary Income',
    amount: income?.amount?.toString() || '',
    pay_frequency: income?.pay_frequency || 'biweekly',
    next_pay_date: income?.next_pay_date?.split('T')[0] || new Date().toISOString().split('T')[0],
    notes: income?.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      income_name: formData.income_name,
      amount: parseFloat(formData.amount),
      pay_frequency: formData.pay_frequency,
      next_pay_date: formData.next_pay_date,
      notes: formData.notes || null
    });
  };

  const canSubmit = formData.income_name && formData.amount && parseFloat(formData.amount) > 0 && formData.next_pay_date;

  // Calculate preview monthly amount
  const monthlyPreview = calculateMonthlyAmount(formData.amount || 0, formData.pay_frequency);

  return (
    <BaseModal 
      isOpen={true} 
      onClose={onClose} 
      title={income ? 'Edit Income' : 'Add Income'}
      icon={<DollarSign size={20} />}
      size="medium"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button 
            type="submit" 
            form="income-form"
            className="btn-primary" 
            disabled={saving || !canSubmit}
          >
            {saving ? (
              <><Loader2 size={16} className="spinner" /> Saving...</>
            ) : (
              <><CheckCircle size={16} /> {income ? 'Save Changes' : 'Add Income'}</>
            )}
          </button>
        </>
      }
    >
      <form id="income-form" onSubmit={handleSubmit}>
        <div className="modal-body">
          {/* Income Name */}
          <div className="form-group">
            <label htmlFor="income_name">Income Name</label>
            <TextInput
              id="income_name"
              value={formData.income_name}
              onChange={(e) => setFormData({ ...formData, income_name: e.target.value })}
              placeholder="e.g., Primary Job, Side Gig"
              disabled={saving}
              required
            />
          </div>

          {/* Amount */}
          <div className="form-group">
            <label htmlFor="amount">Amount Per Paycheck</label>
            <CurrencyInput
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="0.00"
              disabled={saving}
              required
            />
          </div>

          {/* Pay Frequency */}
          <div className="form-group">
            <label htmlFor="pay_frequency">Pay Frequency</label>
            <CustomSelect
              name="pay_frequency"
              value={formData.pay_frequency}
              onChange={(e) => setFormData({ ...formData, pay_frequency: e.target.value })}
              disabled={saving}
              options={[
                { value: 'weekly', label: 'Weekly' },
                { value: 'biweekly', label: 'Every 2 weeks' },
                { value: 'semimonthly', label: 'Twice a month (1st & 15th)' },
                { value: 'monthly', label: 'Monthly' }
              ]}
            />
          </div>

          {/* Next Pay Date */}
          <div className="form-group">
            <label htmlFor="next_pay_date">Next Pay Date</label>
            <DatePicker
              name="next_pay_date"
              value={formData.next_pay_date}
              onChange={(e) => setFormData({ ...formData, next_pay_date: e.target.value })}
              disabled={saving}
            />
            <span className="form-hint">Your next expected payday</span>
          </div>

          {/* Monthly Preview */}
          {formData.amount && parseFloat(formData.amount) > 0 && (
            <div className="info-box">
              <DollarSign size={18} />
              <div>
                <strong>Estimated Monthly Income</strong>
                <p>{formatAmount(monthlyPreview)}</p>
              </div>
            </div>
          )}

          {/* Notes (optional) */}
          <div className="form-group">
            <label htmlFor="notes">Notes (optional)</label>
            <TextInput
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes..."
              disabled={saving}
            />
          </div>
        </div>
      </form>
    </BaseModal>
  );
}

export default IncomeSettings;
